import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AutoUnsubscribe } from '../../auto-unsubscribe.decorator';
import { QuantityCellEditorService } from './quantity-cell-editor.service';
import { FormFieldTextComponent } from '../form-field-text/form-field-text.component';

@AutoUnsubscribe()
@Component({
  imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, TranslatePipe, FormFieldTextComponent],
  selector: 'app-quantity-cell-editor',
  templateUrl: './quantity-cell-editor.component.html',
  styleUrls: ['./quantity-cell-editor.component.scss'],
  standalone: true,
})
export class QuantityCellEditorComponent implements OnInit {
  editing = false;

  @Input()
  compact = false;

  @Input()
  clearable = false;

  @Input()
  mandatory = false;

  @Input()
  label: string;

  @Input()
  quantity = 0;

  @Input()
  min: number = Number.MIN_VALUE;

  @Input()
  max: number = Number.MAX_VALUE;

  @Input()
  resetValue = 0;

  @Input()
  id: string;

  @Input()
  disabled = false;

  @Output()
  editingCancelled: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  editingDone: EventEmitter<number> = new EventEmitter<number>();

  @Output()
  editingStarted: EventEmitter<string> = new EventEmitter<string>();

  private readonly serviceSub: Subscription;
  private formSub: Subscription;

  quantityForm: FormGroup;

  constructor(
    private readonly editorService: QuantityCellEditorService,
    private readonly formBuilder: FormBuilder,
  ) {
    this.serviceSub = editorService.$editingEditor.subscribe((id: string) => {
      if (this.id !== id && this.editing) {
        this.cancel();
      }
    });
  }

  ngOnInit(): void {
    const fieldValidators = [Validators.min(this.min), Validators.max(this.max)];
    if (this.mandatory) {
      fieldValidators.push(Validators.required);
    }

    this.quantityForm = this.formBuilder.group({
      quantity: [this.quantity, Validators.compose(fieldValidators)],
    });
    this.formSub = this.quantityForm.controls['quantity'].valueChanges.subscribe((quantity) => (this.quantity = +quantity));
  }

  shouldDisplayClearBtn(): boolean {
    return this.quantity && this.clearable;
  }

  start(): void {
    this.quantityForm.controls['quantity'].setValue(this.quantity);
    this.editing = true;
    this.editingStarted.next(this.id);
    this.editorService.editingStarted(this.id);
  }

  cancel(): void {
    this.quantity = this.resetValue;
    this.editing = false;
    this.editingCancelled.next(this.id);
    this.editorService.editingDone(this.id);
  }

  done(): void {
    this.editing = false;
    this.editingDone.next(this.quantity);
    this.editorService.editingDone(this.id);
  }

  enterPressed(event: Event): void {
    this.done();
  }

  escapePressed(event: Event): void {
    this.cancel();
  }
}
